import React, { useState, useEffect } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GH_API = API_ROOT.endsWith("/api") ? `${API_ROOT}/guesthouse` : `${API_ROOT}/api/guesthouse`;

const PAYMENT_MODES = [
  { value: "P", label: "Pending" },
  { value: "C", label: "Cash" },
  { value: "Q", label: "Cheque" },
  { value: "O", label: "Online" },
  { value: "F", label: "Free Charge" }
];

function calcNAmount(f) {
  const amount = parseFloat(f.Amount)          || 0;
  const extra  = parseFloat(f.ExtraBedCharges) || 0;
  const penal  = parseFloat(f.PenalCharges)    || 0;
  const disc   = parseFloat(f.Discount)        || 0;
  const gst    = parseFloat(f.GST)             || 0;
  const base   = amount + extra + penal;
  return Math.round(base + (base * gst) / 100 - disc);
}

function fmtDate(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

const EMPTY = {
  VisitorName: "", Mobile: "", Relationship: "", BookingRef: "", Status: "",
  UnitRent: "", GST: "", Amount: "", NoOfDays: "", ExtraBedCharges: "",
  PenalCharges: "", Discount: "", NarrationMisc: "", PaymentMode: "P",
  UPIID: "", ReceiptDate: "", GSTIN: "", SrcYear: "", NAmount: "",
  FromDate: "", ToDate: "", GuestHouseID: ""
};

export default function GuesthouseVisitorEditBooking({ bookingId, visitorId, bookingRef, moduleId, onClose, onSaved }) {
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form,       setForm]       = useState(EMPTY);
  const [logbook,    setLogbook]    = useState(null);
  const [invoiceNo,  setInvoiceNo]  = useState("");

  useEffect(() => {
    if (!bookingId || visitorId == null) return;
    setLoading(true);
    setError("");
    fetch(`${GH_API}/booking/admin-edit-visitor/${bookingId}/${visitorId}?moduleId=${moduleId}`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.message || "Failed to load.");
        return d;
      })
      .then(data => {
        const v   = data.visitor  || {};
        const b   = data.booking  || {};
        const inv = data.invoice  || {};
        const nn  = (val) => (val != null ? String(val) : "");
        const init = {
          VisitorName:     v.VisitorName    || "",
          Mobile:          v.Mobile         || "",
          Relationship:    v.Relationship   || "",
          BookingRef:      v.BookingRef     || bookingRef || "",
          Status:          v.Status         || "",
          UnitRent:        nn(v.UnitRent),
          GST:             nn(v.GST),
          Amount:          nn(v.Amount),
          NoOfDays:        nn(v.NoOfDays),
          ExtraBedCharges: nn(v.ExtraBedCharges),
          PenalCharges:    nn(v.PenalCharges),
          Discount:        nn(v.Discount),
          NarrationMisc:   v.NarrationMisc  || "",
          PaymentMode:     v.PaymentMode    || "P",
          UPIID:           v.UPIID          || "",
          ReceiptDate:     fmtDate(v.ReceiptDate),
          GSTIN:           v.GSTIN          || "",
          SrcYear:         v.SrcYear        || "",
          NAmount:         "",
          FromDate:        fmtDate(b.FromDate),
          ToDate:          fmtDate(b.ToDate),
          GuestHouseID:    nn(b.GuestHouseID)
        };
        init.NAmount = String(calcNAmount(init));
        setForm(init);
        setLogbook(data.logbook || null);
        setInvoiceNo(inv.SerialNumber ? String(inv.SerialNumber) : "");
      })
      .catch(err => setError(err.message || "Failed to load visitor data."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, visitorId]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (["Amount", "ExtraBedCharges", "PenalCharges", "Discount", "GST"].includes(name)) {
        next.NAmount = String(calcNAmount(next));
      }
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const res = await fetch(
        `${GH_API}/booking/admin-edit-visitor/${bookingId}/${visitorId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            VisitorName:     form.VisitorName,
            Mobile:          form.Mobile,
            Relationship:    form.Relationship,
            UnitRent:        form.UnitRent,
            GST:             form.GST,
            Amount:          form.Amount,
            NoOfDays:        form.NoOfDays,
            ExtraBedCharges: form.ExtraBedCharges,
            PenalCharges:    form.PenalCharges,
            Discount:        form.Discount,
            NarrationMisc:   form.NarrationMisc,
            PaymentMode:     form.PaymentMode,
            UPIID:           form.UPIID,
            ReceiptDate:     form.ReceiptDate,
            GSTIN:           form.GSTIN,
            SrcYear:         form.SrcYear
          })
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update.");
      setSuccess(data.message || "Visitor booking updated successfully.");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message || "Failed to update.");
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyCheckedIn  = logbook ? logbook.CheckIn  !== null : false;
  const alreadyCheckedOut = logbook ? logbook.CheckOut !== null : false;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">

          <div className="modal-header py-2">
            <h5 className="modal-title fw-bold">
              Change Booking Request (By Admin) — Additional Visitor
              {form.BookingRef && (
                <span className="ms-2 badge bg-secondary" style={{ fontSize: "0.85em" }}>
                  Ref: {form.BookingRef}
                </span>
              )}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading   && <div className="alert alert-info mb-2">Loading...</div>}
            {error     && <div className="alert alert-danger mb-2">{error}</div>}
            {success   && <div className="alert alert-success mb-2">{success}</div>}

            {!loading && (
              <form onSubmit={onSubmit}>
                <div className="row g-2">

                  {/* ── Row 1: Identity (read-only display + Relationship) ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Booking ID</label>
                    <input className="form-control form-control-sm" value={`${bookingId}${form.BookingRef || ""}`} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Booking Ref</label>
                    <input className="form-control form-control-sm" value={form.BookingRef} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Guest House ID</label>
                    <input className="form-control form-control-sm" value={form.GuestHouseID} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Status</label>
                    <input className="form-control form-control-sm" value={form.Status} readOnly disabled />
                  </div>

                  {/* ── Row 2: Visitor details ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Visitor Name</label>
                    <input className="form-control form-control-sm" name="VisitorName" value={form.VisitorName} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Mobile</label>
                    <input className="form-control form-control-sm" name="Mobile" value={form.Mobile} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Relationship</label>
                    <input className="form-control form-control-sm" name="Relationship" value={form.Relationship} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Check-In (From Date)</label>
                    <input className="form-control form-control-sm" type="date" value={form.FromDate} readOnly disabled />
                  </div>

                  {/* ── Row 3: Dates / Logbook ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Check-Out (To Date)</label>
                    <input className="form-control form-control-sm" type="date" value={form.ToDate} readOnly disabled />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">
                      Actual Check-In
                      {alreadyCheckedIn && <span className="ms-1 text-success">(Done)</span>}
                    </label>
                    <input
                      className="form-control form-control-sm"
                      value={alreadyCheckedIn ? fmtDate(logbook.CheckIn) : ""}
                      readOnly disabled
                    />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">
                      Actual Check-Out
                      {alreadyCheckedOut && <span className="ms-1 text-success">(Done)</span>}
                    </label>
                    <input
                      className="form-control form-control-sm"
                      value={alreadyCheckedOut ? fmtDate(logbook.CheckOut) : ""}
                      readOnly disabled
                    />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Invoice Serial No.</label>
                    <input className="form-control form-control-sm" value={invoiceNo} readOnly disabled />
                  </div>

                  {/* ── Row 4: Payment ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Payment Mode</label>
                    <select className="form-select form-select-sm" name="PaymentMode" value={form.PaymentMode} onChange={onChange}>
                      {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Transaction ID (UPIID)</label>
                    <input className="form-control form-control-sm" name="UPIID" value={form.UPIID} onChange={onChange} placeholder="Sets UPIID → triggers invoice" />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Receipt Date</label>
                    <input className="form-control form-control-sm" type="date" name="ReceiptDate" value={form.ReceiptDate} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Unit Rent</label>
                    <input className="form-control form-control-sm" name="UnitRent" value={form.UnitRent} onChange={onChange} />
                  </div>

                  {/* ── Row 5: Amounts ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Amount</label>
                    <input className="form-control form-control-sm" name="Amount" value={form.Amount} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">No. of Days</label>
                    <input className="form-control form-control-sm" name="NoOfDays" value={form.NoOfDays} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">GST %</label>
                    <input className="form-control form-control-sm" name="GST" value={form.GST} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Extra Bed Charges</label>
                    <input className="form-control form-control-sm" name="ExtraBedCharges" value={form.ExtraBedCharges} onChange={onChange} />
                  </div>

                  {/* ── Row 6: Misc ── */}
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Misc Charges</label>
                    <input className="form-control form-control-sm" name="PenalCharges" value={form.PenalCharges} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Discount Amt</label>
                    <input className="form-control form-control-sm" name="Discount" value={form.Discount} onChange={onChange} />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Net Amount</label>
                    <input className="form-control form-control-sm" value={form.NAmount} readOnly />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">GSTIN</label>
                    <input className="form-control form-control-sm" name="GSTIN" value={form.GSTIN} onChange={onChange} />
                  </div>

                  {/* ── Row 7: Narration / SrcYear ── */}
                  <div className="col-sm-6">
                    <label className="form-label mb-1 small">Narration for Misc Charges</label>
                    <input className="form-control form-control-sm" name="NarrationMisc" value={form.NarrationMisc} onChange={onChange} placeholder="Narration Misc" />
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label mb-1 small">Source Year</label>
                    <input className="form-control form-control-sm" name="SrcYear" value={form.SrcYear} onChange={onChange} placeholder="e.g. 2024" />
                  </div>

                </div>

                <div className="mt-3 d-flex gap-2">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                    {submitting ? "Saving…" : "Save"}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                    Close
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
